import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';

const LocationContext = createContext(null);

export const useLocation = () => useContext(LocationContext);

export const LocationProvider = ({ children }) => {
  const { business, isAuthenticated } = useAuth();
  const [locations, setLocations] = useState([]);      // all locations for this business
  const [activeLocation, setActiveLocationState] = useState(null); // currently selected
  const [userRole, setUserRole] = useState('owner');    // 'owner' | 'manager' | 'staff'
  const [loadingLoc, setLoadingLoc] = useState(true);

  // Load locations and determine role
  const loadLocations = useCallback(async () => {
    if (!business?.id) { setLoadingLoc(false); return; }
    setLoadingLoc(true);
    try {
      // 1. Check if this auth user is a branch manager (not owner)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const isOwner = business.owner_id === user.id;

      if (!isOwner) {
        // Manager: load assigned location
        const { data: bu } = await supabase
          .from('business_users')
          .select('id, role, location_id')
          .eq('auth_user_id', user.id)
          .eq('business_id', business.id)
          .single();

        if (bu?.location_id) {
          const { data: loc } = await supabase
            .from('business_locations')
            .select('*')
            .eq('id', bu.location_id)
            .single();

          if (loc) {
            setLocations([loc]);
            setActiveLocationState(loc);
          }
        }
        setUserRole(bu?.role || 'manager');
        setLoadingLoc(false);
        return;
      }

      // Owner: load all locations
      setUserRole('owner');
      const { data: locs } = await supabase
        .from('business_locations')
        .select('*')
        .eq('business_id', business.id)
        .order('is_main', { ascending: false })
        .order('created_at', { ascending: true });

      const allLocs = locs || [];
      setLocations(allLocs);

      // Restore last selected location from localStorage
      const savedId = localStorage.getItem(`active_location_${business.id}`);
      const saved = allLocs.find(l => l.id === savedId);
      setActiveLocationState(saved || allLocs[0] || null);

    } catch (e) {
      console.error('LocationContext error:', e);
    } finally {
      setLoadingLoc(false);
    }
  }, [business]);

  useEffect(() => {
    if (isAuthenticated) loadLocations();
  }, [isAuthenticated, loadLocations]);

  const setActiveLocation = (loc) => {
    setActiveLocationState(loc);
    if (business?.id) {
      localStorage.setItem(`active_location_${business.id}`, loc?.id || '');
    }
  };

  const refreshLocations = () => loadLocations();

  return (
    <LocationContext.Provider value={{
      locations,
      activeLocation,
      setActiveLocation,
      userRole,
      loadingLoc,
      refreshLocations,
      isOwner: userRole === 'owner',
      // Helper: the ID to use in DB queries
      locationId: activeLocation?.id || null,
    }}>
      {children}
    </LocationContext.Provider>
  );
};
